import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../../../../utils/api';
import { 
  TaxonomyLabelReviewProps, 
  TaxonomyLabel, 
  parseLabelKey,
  FeedbackSentiment
} from './types';
import DimensionSection from './DimensionSection';
import { FiThumbsUp, FiMinus, FiThumbsDown } from 'react-icons/fi';
import IconWrapper from '../../../IconWrapper';
import { useTaxonomySchema } from '../../../../hooks/useTaxonomySchema';

const TaxonomyLabelReview: React.FC<TaxonomyLabelReviewProps> = ({ 
  classification_id,
  taxonomy_classification,
  onClose,
  signal_id,
  signalSummary,
  signalVerbatim,
  customerName,
  conductScore
}) => {
  const [incorrectLabels, setIncorrectLabels] = useState<Set<string>>(new Set());
  const [missingLabels, setMissingLabels] = useState<Array<{dimension: string; category: string; label: string}>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comprehensiveSummary, setComprehensiveSummary] = useState<string | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [freeformText, setFreeformText] = useState('');
  const [selectedSentiment, setSelectedSentiment] = useState<FeedbackSentiment | null>(null);
  
  // Get taxonomy schema dynamically
  const taxonomyVersion = taxonomy_classification?.taxonomy_version;
  const { dimensions, getDimensionColor, getDimensionDisplayName } = useTaxonomySchema(taxonomyVersion);

  // Fetch comprehensive summary when component mounts
  useEffect(() => {
    const fetchComprehensiveSummary = async () => {
      if (!signal_id) return;
      
      setIsLoadingSummary(true);
      try {
        const response = await fetchWithAuth(`/v1/signals/${signal_id}/comprehensive-summary`);
        if (response.comprehensive_summary) {
          setComprehensiveSummary(response.comprehensive_summary);
        }
      } catch (error) {
        console.error('Failed to fetch comprehensive summary:', error);
        // Don't show error toast, just use the basic summary
      } finally {
        setIsLoadingSummary(false);
      }
    };
    
    fetchComprehensiveSummary();
  }, [signal_id]);

  // Extract and group labels from the hierarchical taxonomy structure
  const labelsByDimension = useMemo(() => {
    const result: Record<string, TaxonomyLabel[]> = {};
    
    // Initialize all dimensions from schema with empty arrays to ensure they all appear
    dimensions.forEach(dimension => {
      result[dimension.key] = [];
    });
    
    if (!taxonomy_classification?.dimensions) {
      return result;
    }

    // Traverse the hierarchical structure and populate with detected labels
    Object.entries(taxonomy_classification.dimensions).forEach(([dimensionKey, dimension]: [string, any]) => {
      const labels: TaxonomyLabel[] = [];
      
      if (dimension.categories) {
        Object.entries(dimension.categories).forEach(([categoryKey, category]: [string, any]) => {
          if (category.labels) {
            Object.entries(category.labels).forEach(([labelKey, labelData]: [string, any]) => {
              if (labelData && labelData.label_name) {
                labels.push({
                  label_name: labelData.label_name,
                  confidence: labelData.confidence || 0,
                  rationale: labelData.rationale || '',
                  regulation_reference: labelData.regulation_reference,
                  dimension: dimensionKey,
                  category: categoryKey
                });
              }
            });
          }
        });
      }
      
      // Update the dimension with detected labels (overwrite the empty array)
      if (labels.length > 0) {
        result[dimensionKey] = labels;
      }
    });
    
    return result;
  }, [taxonomy_classification, dimensions]);

  const handleToggleIncorrect = (labelKey: string) => {
    setIncorrectLabels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(labelKey)) {
        newSet.delete(labelKey);
      } else {
        newSet.add(labelKey);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    const hasLabelFeedback = incorrectLabels.size > 0 || missingLabels.length > 0;
    const hasFreeformFeedback = freeformText.trim() && selectedSentiment;
    
    if (!hasLabelFeedback && !hasFreeformFeedback) {
      toast.warning('Please provide feedback by marking incorrect labels, adding missing ones, or providing general feedback');
      return;
    }
    
    if (freeformText.trim() && !selectedSentiment) {
      toast.warning('Please select a sentiment for your general feedback');
      return;
    }

    setIsSubmitting(true);
    try {
      const incorrectLabelsList = Array.from(incorrectLabels).map(parseLabelKey);
      
      const payload: any = {
        incorrect_labels: incorrectLabelsList,
        missing_labels: missingLabels
      };
      
      if (freeformText.trim() && selectedSentiment) {
        payload.freeform_feedback = {
          text: freeformText.trim(),
          sentiment: selectedSentiment
        };
      }
      
      await fetchWithAuth(`/v1/feedback/taxonomy-labels/${classification_id}`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      toast.success('Thank you! Your feedback helps improve our classification accuracy');
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleIncorrect = (labelKey: string) => {
    const newIncorrect = new Set(incorrectLabels);
    if (newIncorrect.has(labelKey)) {
      newIncorrect.delete(labelKey);
    } else {
      newIncorrect.add(labelKey);
    }
    setIncorrectLabels(newIncorrect);
  };

  const handleAddMissingLabel = (label: {dimension: string; category: string; label: string}) => {
    // Check if label already exists in missing labels
    const exists = missingLabels.some(
      ml => ml.dimension === label.dimension && 
            ml.category === label.category && 
            ml.label === label.label
    );
    
    if (!exists) {
      setMissingLabels([...missingLabels, label]);
    }
  };

  const handleRemoveMissingLabel = (index: number) => {
    setMissingLabels(missingLabels.filter((_, i) => i !== index));
  };

  const hasLabels = Object.keys(labelsByDimension).length > 0;
  const hasChanges = incorrectLabels.size > 0 || missingLabels.length > 0 || (freeformText.trim() && selectedSentiment);

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-50 text-left align-middle shadow-xl transition-all">
                <div className="bg-white px-6 py-4 border-b border-gray-200">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    Taxonomy Label Review
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    Review the classification labels below. Click on any incorrect labels to mark them for feedback.
                  </p>
                </div>
                
                {/* Signal Context */}
                {(signalSummary || signalVerbatim || customerName || comprehensiveSummary) && (
                  <div className="mx-6 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    {customerName && (
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Customer: {customerName}
                      </p>
                    )}
                    {signalSummary && !comprehensiveSummary && (
                      <h4 className="text-base font-semibold text-gray-900 mb-2">
                        {signalSummary}
                      </h4>
                    )}
                    {comprehensiveSummary ? (
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Comprehensive Summary</h4>
                        {isLoadingSummary ? (
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-gray-600">Generating comprehensive summary...</span>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-700 space-y-2 whitespace-pre-wrap">
                            {comprehensiveSummary}
                          </div>
                        )}
                      </div>
                    ) : (
                      signalVerbatim && (
                        <p className="text-sm text-gray-600 italic line-clamp-3">
                          "{signalVerbatim}"
                        </p>
                      )
                    )}
                    {conductScore !== undefined && (
                      <div className="mt-2 flex items-center">
                        <span className="text-sm text-gray-500 mr-2">Conduct Score:</span>
                        <span className={`text-lg font-bold ${
                          conductScore >= 8 ? 'text-red-600' :
                          conductScore >= 6 ? 'text-orange-600' :
                          conductScore >= 4 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {conductScore.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  {hasLabels ? (
                    // Dimensions are already sorted by the schema
                    dimensions.map(dimension => (
                      <DimensionSection
                        key={dimension.key}
                        dimension={dimension.key}
                        labels={labelsByDimension[dimension.key] || []}
                        color={dimension.color}
                        incorrectLabels={incorrectLabels}
                        onToggleIncorrect={toggleIncorrect}
                        missingLabels={missingLabels}
                        onAddMissingLabel={handleAddMissingLabel}
                        onRemoveMissingLabel={handleRemoveMissingLabel}
                      />
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No classification labels found.</p>
                    </div>
                  )}
                </div>
                
                {/* Freeform Feedback Section */}
                <div className="px-6 pb-6">
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      General Feedback (Optional)
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Share any additional thoughts or suggestions about this classification
                    </p>
                    
                    <textarea
                      value={freeformText}
                      onChange={(e) => setFreeformText(e.target.value)}
                      placeholder="Your feedback helps us improve our classification accuracy..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      rows={3}
                    />
                    
                    {freeformText.trim() && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-700 mb-2">
                          How do you feel about this classification overall? <span className="text-red-500">*</span>
                        </p>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setSelectedSentiment('positive')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-colors ${
                              selectedSentiment === 'positive'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <IconWrapper icon={FiThumbsUp} className="h-5 w-5" />
                            <span className="text-sm font-medium">Good</span>
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => setSelectedSentiment('neutral')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-colors ${
                              selectedSentiment === 'neutral'
                                ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <IconWrapper icon={FiMinus} className="h-5 w-5" />
                            <span className="text-sm font-medium">Neutral</span>
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => setSelectedSentiment('negative')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-colors ${
                              selectedSentiment === 'negative'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <IconWrapper icon={FiThumbsDown} className="h-5 w-5" />
                            <span className="text-sm font-medium">Poor</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {incorrectLabels.size > 0 && (
                      <span>{incorrectLabels.size} label{incorrectLabels.size !== 1 ? 's' : ''} marked as incorrect</span>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !hasChanges}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TaxonomyLabelReview;